import { defineStore } from 'pinia';
import { useState } from '@/composables/common/useState';
import HouseService from '@/services/house.service';
import i18n from '@/i18n';
import { HBL_DEFAULT_FILTERS } from '@/constants/house-bl';
import { HblEntity } from '@/types/hbl/hbl.entity';
import { MilestoneEntity } from '@/types/common/milestone.entity';
import { HblFilters } from '@/types/hbl/hbl.filters';
import { useFilters } from '@/composables/filters/useFilters';
import { useToggle } from '@/composables/useToggle';
import { AxiosError } from 'axios';
import { useToast } from '@/composables/useToast';
import { useManifestHasInvoicedCharges } from '@/composables/charges/manifest/useManifestHasInvoicedCharges';
import { ManifestServiceType } from '@/types/manifest/manifest.types';
import { useVirtualHblStore } from '@/store/modules/house-bl/useVirtualHblStore';
import { useOnlineStore } from '../useOnlineStore';
import { watch } from 'vue';

export interface CreateHblPayload {
  mblId: string;
  mblNumber: string;
  payload: HblEntity;
  service: ManifestServiceType;
}

export const useHBLStore = defineStore('HBL', () => {
  const virtualHblStore = useVirtualHblStore();

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [readonly, toggleReadonly] = useToggle(true);
  const [hblItemsByMblId, setHblItemsByMblId] = useState<HblEntity[]>([]);
  const [hbl, setHbl] = useState<HblEntity | null>(null);
  const [allHbl, setAllHbl] = useState<HblEntity[]>([]);
  const [milestones, setMilestones] = useState<MilestoneEntity[]>([]);
  const [isUpdatingHbl, toggleUpdatingHbl] = useToggle();

  const { hasInvoicedCharges, checkManifestHasInvoicedCharges } = useManifestHasInvoicedCharges();

  const toast = useToast();

  const filtersModule = useFilters<HblEntity, Partial<HblFilters>>(HouseService, {
    initialFilters: HBL_DEFAULT_FILTERS(),
    watcher: true,
  });

  const create = async ({ mblNumber, payload, mblId, service }: CreateHblPayload) => {
    const { data: result } = await HouseService.create({
      ...payload,
      mblNumber: payload.mblNumber || mblNumber,
      mblId,
      service,
    });
    if (!result) {
      return { success: false, message: result.message || i18n.t('ServerErrorTryAgainLater') };
    }
    return { success: true, message: i18n.t('HBLCreatedSuccessfully'), data: result };
  };

  const update = async ({ id, payload }: { id: string; payload: Partial<HblEntity> }) => {
    try {
      toggleUpdatingHbl(true);
      const { data: result } = await HouseService.update({ ...payload, _id: id });
      if (!result) {
        return { success: false, message: result.message || i18n.t('ServerErrorTryAgainLater') };
      }
      await fetchHblById(id);

      return { success: true, message: i18n.t('HBLSavedSuccessfully') };
    } catch (err) {
      throw new Error((err as AxiosError).response?.data as string);
    } finally {
      toggleUpdatingHbl(false);
    }
  };

  const fetchHblItemsByMblId = async (mblId: string) => {
    try {
      const { data } = await HouseService.getAll(mblId);
      setHblItemsByMblId(data);
    } catch (e) {
      console.log('fetchHblItemsByMblId', e);
      throw e;
    }
  };

  const deleteHbl = async (id: string) => {
    try {
      await HouseService.delete(id);
      await virtualHblStore.search();
      // await filtersModule.fetchFilteredItems();
    } catch (error) {
      toast.error((error as AxiosError).response?.data as string);
      throw error;
    }
  };

  const fetchHblById = async (id: string) => {
    try {
      const { data } = await HouseService.getOneById(id);
      setHbl(data);
    } catch (e) {
      console.log('fetchHblById', e);
      throw e;
    }
  };

  const fetchAllHblByMblId = async (id: string) => {
    try {
      const { data } = await HouseService.getAll(id);
      setAllHbl(data);
    } catch (e) {
      console.log('fetchAllHblByMblId', e);
    }
  };

  const fetchMilestones = async (id: string) => {
    try {
      const { data } = await HouseService.getMilestones(id);
      setMilestones(data);
    } catch (e) {
      console.log('fetchMilestones', e);
    }
  };

  const validate = async (id: string) => {
    try {
      const { data } = await HouseService.validateStatus(id);
      if (data.hblStatus) toast.success('HBL is valid!');
      else {
        let message =
          'HBL is not valid! <br /> Discrepancy between Received and Manifested values: <br />';
        if (!data.isPcsValid) message = message.concat('PCS, ');
        if (!data.isWeightValid) message = message.concat('Weight, ');
        if (!data.isVolumeValid) message = message.concat('Volume');
        toast.error(message);
      }
      return data;
    } catch (e) {
      console.log(e);
    }
  };

  const onlineStore = useOnlineStore();

  watch(readonly, (value) => {
    value ? onlineStore.stopUserPageEdit() : onlineStore.startUserPageEdit();
  });

  return {
    hbl,
    allHbl,
    milestones,
    isUpdatingHbl,

    hasChanges,
    setHasChanges,
    hasInvoicedCharges,
    checkManifestHasInvoicedCharges,

    readonly,
    toggleReadonly,

    hblItemsByMblId,
    create,
    update,
    setHbl,
    deleteHbl,
    fetchHblById,
    fetchMilestones,
    fetchAllHblByMblId,
    fetchHblItemsByMblId,
    validate,

    ...filtersModule,
  };
});
